import React from 'react';
import { Nav } from 'react-bootstrap';
import {
  Switch,
  Route,
  Redirect,
  NavLink,
  useRouteMatch,
} from 'react-router-dom';

import { fetchTreeInsightsEnabled } from '../../components/model/rgipChatApi';
import PerformanceCheckPage from './PerformanceCheckPage';
import ReviewSignalsPage from './ReviewSignalsPage';
import SpotCheckPage from './SpotCheckPage';
import ScoreLookupPage from './ScoreLookupPage';

type MatchParams = {
  tenant: string;
  modelId: string;
};

/**
 * `isCustomerFit` gates the score lookup, and only the score lookup.
 *
 * `lookupScore` fixes its observations table to `getDataSetTableName('training',
 * 'customer_fit')` and its score scale to `getCustomerFitMapping()`, while `/review`
 * is mounted for every model type (unlike `/insights`, which branches). On an MQA or
 * PQL model the tab therefore offered to score against another model type's table.
 *
 * Gated rather than generalised: `apply_smoothing_ltb_mqa.sql` and its PQL sibling
 * exist, so the page CAN be taught the other bases, but that is a feature with its own
 * testing and not a thing to infer from the presence of a template.
 */
const ReviewPage = ({ isCustomerFit = true }: { isCustomerFit?: boolean }) => {
  const match = useRouteMatch<MatchParams>();
  const { tenant, modelId } = match.params;
  const basePath = `/tenant/${tenant}/models/${modelId}/review`;

  // Null while resolving, so neither shape of the nav renders and the tabs do
  // not flicker from one to the other. Same gate as `Overview.tsx`.
  const [isMerged, setIsMerged] = React.useState<boolean | null>(null);
  React.useEffect(() => {
    fetchTreeInsightsEnabled(tenant)
      .catch(() => false)
      .then(setIsMerged);
  }, [tenant]);

  if (isMerged === null) return null;

  return (
    <>
      <Nav className="third-level-nav review bg-white">
        {/* Merged, Sample is not a destination: the signals and the sample they
            render are one page. Unmerged, this is exactly what ships today. */}
        {isMerged ? (
          <Nav.Link
            className="text-dark"
            as={NavLink}
            to={`${basePath}/signals`}
          >
            Signals
          </Nav.Link>
        ) : (
          <Nav.Link
            className="text-dark"
            as={NavLink}
            to={`${basePath}/sample`}
          >
            Sample
          </Nav.Link>
        )}
        <Nav.Link
          className="text-dark"
          as={NavLink}
          to={`${basePath}/performance`}
        >
          Performance
        </Nav.Link>
        {/* Scoring one record against the model as it stands. Only on the
            revamp: it is a new surface, and off the flag this page renders
            exactly the two tabs it renders today. */}
        {isMerged && isCustomerFit && (
          <Nav.Link
            className="text-dark"
            as={NavLink}
            to={`${basePath}/score_lookup`}
          >
            Score lookup
          </Nav.Link>
        )}
      </Nav>
      <Switch>
        {isMerged && (
          <Route path={`${match.path}/signals`}>
            <ReviewSignalsPage />
          </Route>
        )}
        {isMerged && isCustomerFit && (
          <Route path={`${match.path}/score_lookup`}>
            <ScoreLookupPage />
          </Route>
        )}
        {/* Kept as a redirect rather than removed. The old path is linked from
            elsewhere in the product and sits in people's bookmarks. */}
        <Route path={`${match.path}/sample`}>
          {isMerged ? (
            <Redirect to={`${basePath}/signals`} />
          ) : (
            <SpotCheckPage />
          )}
        </Route>
        <Route path={`${match.path}/performance`}>
          <PerformanceCheckPage revamped={isMerged} />
        </Route>
        <Redirect
          from={match.path}
          to={`${match.path}/${isMerged ? 'signals' : 'sample'}`}
        />
      </Switch>
    </>
  );
};

export default ReviewPage;
